import React from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { haptic } from '../services/telegram';
import { playSound } from '../services/audio';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { BoltIcon } from '../components/icons/BoltIcon';
import { SoundIcon } from '../components/icons/SoundIcon';

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  desc: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ icon, label, desc, value, onChange }: ToggleRowProps) {
  return (
    <div className="arena-data-row flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center" style={{ width: 20, height: 20 }}>
          {icon}
        </span>
        <div>
          <div className="font-semibold text-sm text-text-primary">{label}</div>
          <div className="text-xs text-text-secondary">{desc}</div>
        </div>
      </div>
      <motion.button
        data-nav
        style={{
          width: '52px',
          height: '28px',
          borderRadius: '14px',
          background: value
            ? 'linear-gradient(90deg, oklch(57% 0.15 145), oklch(78% 0.12 145))'
            : 'oklch(10% 0.035 252 / 0.7)',
          border: value ? '1px solid oklch(57% 0.15 145)' : '1px solid oklch(43% 0.055 252 / 0.68)',
          position: 'relative',
          flexShrink: 0,
        }}
        onClick={() => {
          haptic.selection();
          const next = !value;
          onChange(next);
          if (next) playSound('moveSelect');
        }}
      >
        <motion.div
          className="absolute top-0.5 w-6 h-6 rounded-full"
          style={{
            background: value ? 'oklch(14% 0.045 252)' : 'oklch(59% 0.035 86)',
          }}
          animate={{ x: value ? 24 : 2 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.button>
    </div>
  );
}

export function SettingsScreen() {
  const {
    boostEnabled,
    soundEnabled,
    setBoostEnabled,
    setSoundEnabled,
    setScreen,
  } = useGameStore();

  return (
    <div className="game-home-stage flex flex-col h-full overflow-y-auto scrollbar-hide">
      <div className="flex flex-col gap-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            data-nav
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            aria-label="Back to home"
            style={{ background: 'oklch(21% 0.045 252 / 0.9)', border: '1px solid oklch(43% 0.055 252 / 0.72)', boxShadow: '0 8px 18px oklch(3% 0.02 252 / 0.42)' }}
            onClick={() => setScreen('home')}
          >
            <ArrowLeftIcon size={18} className="text-text-secondary" />
          </button>
          <h1 className="text-xl font-black text-text-primary">Settings</h1>
        </div>

        {/* Gameplay toggles */}
        <motion.div
          className="arena-page-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="arena-section-label mb-1">
            Gameplay
          </div>
          <div>
            <ToggleRow
              icon={<BoltIcon size={20} className="text-gold" />}
              label="Energy Boost"
              desc="Start with +20 energy for +10% match cost"
              value={boostEnabled}
              onChange={setBoostEnabled}
            />
            <ToggleRow
              icon={<SoundIcon size={20} className="text-water-light" />}
              label="Sound Effects"
              desc="Game sounds and haptic feedback"
              value={soundEnabled}
              onChange={setSoundEnabled}
            />
          </div>
        </motion.div>

        {/* About */}
        <motion.div
          className="arena-page-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="arena-section-label mb-3">
            About
          </div>
          <div className="flex flex-col gap-2 text-sm text-text-secondary">
            <div className="flex justify-between">
              <span>App</span>
              <span className="text-text-primary font-medium">Elmental TMA</span>
            </div>
            <div className="flex justify-between">
              <span>Version</span>
              <span className="text-text-primary font-medium">0.0.1</span>
            </div>
            <div className="flex justify-between">
              <span>Server</span>
              <span className="text-water font-medium">SpacetimeDB</span>
            </div>
          </div>

          <div
            className="mt-4 pt-4 border-t border-bg-border text-xs text-text-muted text-center"
          >
            Strategic elemental battles in realtime PvP.{'\n'}
            Rock-Paper-Scissors evolved.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
